import React from 'react';
import { RenderRequest } from 'types/cx-editor';
import { Box, Typography, useTheme, Card, CardHeader } from '@mui/material';
import { useWidgetSdk } from 'context/WidgetSdkContext';
import { WidgetErrorBoundary } from './WidgetErrorBoundary';
import { MobilePreview } from './MobilePreview';
import { WebWidgetMobileIframe } from './WebWidgetMobileIframe';
import { initializeAndRenderWidget } from 'utils/widget-renderer';
import { getWidgetIdFromTemplate, getWebWidgetDesktopWidth, getWebWidgetDisplayName } from 'constants/web-widget-mapping';
import { useSelector } from 'react-redux';
import { RootState } from 'store/Store';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { selectScenariosForTemplate, selectDataScenariosLoading } from 'store/dataReducers/dataScenariosSlice';
import { toast } from 'utils/toast';

/** iPhone 17 Pro Max frame (440×956 viewport, 14px bezel). */
const MOBILE_PHONE = { width: 440, height: 956, bezel: 14 } as const;

/** Max scale so the phone never looks oversized on large screens (more like a real iPhone). */
const MAX_PHONE_SCALE = 0.7;

/** Mobile viewport CSS: forces entire page to fit inside device frame, prevents horizontal overflow. */
const EMAIL_MOBILE_CSS = `
  *, *::before, *::after { box-sizing: border-box !important; }
  html {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: 100%;
    margin: 0;
    padding: 0;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    -webkit-text-size-adjust: 100%;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: -ms-autohiding-scrollbar;
  }
  body {
    margin: 0 !important;
    padding: 0 !important;
    padding-top: 44px !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    min-height: 100%;
    overflow-x: hidden !important;
    overflow-y: visible;
    overflow-wrap: break-word !important;
    word-wrap: break-word !important;
    word-break: break-word;
    /* Do not set font-family: email inline styles (Arial, Poppins, etc.) must win to avoid different line breaks */
  }
  body > * {
    max-width: 100% !important;
    min-width: 0 !important;
  }
  img {
    max-width: 100% !important;
    width: auto !important;
    height: auto !important;
    display: block;
  }
  table, table[width] {
    max-width: 100% !important;
    width: 100% !important;
    min-width: 0 !important;
  }
  td, th, td[width], th[width] {
    max-width: 100% !important;
    overflow-wrap: break-word !important;
    word-wrap: break-word !important;
    word-break: break-word;
  }
  .wrapper, .outer, .webkit, .email-container, .content,
  center[class="wrapper"], div[class="webkit"], table[class="outer"],
  table[class="wrapper"], div[class="outer"], div[class="wrapper"],
  [class*="wrapper"], [class*="container"], [class*="content"],
  table[width], td[width], th[width] {
    max-width: 100% !important;
    min-width: 0 !important;
  }
  pre, code {
    max-width: 100% !important;
    overflow-x: auto !important;
    white-space: pre-wrap !important;
    word-wrap: break-word !important;
  }
  a, button, input, select, textarea {
    max-width: 100% !important;
  }
  div, section, article, main, aside, header, footer, span, p {
    max-width: 100% !important;
    min-width: 0 !important;
  }
  [style*="flex"], [style*="display: flex"], .flex, [class*="flex"] {
    flex-wrap: wrap !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
  [style*="flex"] > *, [style*="display: flex"] > *, .flex > *, [class*="flex"] > * {
    min-width: 0 !important;
    flex-shrink: 1 !important;
  }

  /* User Feedback (FEEDBACK_V4, FEEDBACK_V3, FEEDBACK): keep question + icons on same line */
  tr[id$="FEEDBACK_V4"] div,
  tr[id$="FEEDBACK_V4"] table,
  tr[id$="FEEDBACK_V3"] div,
  tr[id$="FEEDBACK_V3"] table,
  tr[id$="FEEDBACK"] div,
  tr[id$="FEEDBACK"] table {
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  tr[id$="FEEDBACK_V4"] tr,
  tr[id$="FEEDBACK_V3"] tr,
  tr[id$="FEEDBACK"] tr {
    display: table-row !important;
  }
  tr[id$="FEEDBACK_V4"] td,
  tr[id$="FEEDBACK_V3"] td,
  tr[id$="FEEDBACK"] td {
    display: table-cell !important;
    vertical-align: middle !important;
    box-sizing: border-box !important;
    padding: 12px 16px 0 !important;
  }
  tr[id$="FEEDBACK_V4"] td:first-of-type,
  tr[id$="FEEDBACK_V3"] td:first-of-type,
  tr[id$="FEEDBACK"] td:first-of-type {
    white-space: normal !important;
    width: auto !important;
    min-width: 0 !important;
  }
  tr[id$="FEEDBACK_V4"] td:not(:first-of-type),
  tr[id$="FEEDBACK_V3"] td:not(:first-of-type),
  tr[id$="FEEDBACK"] td:not(:first-of-type) {
    white-space: nowrap !important;
    width: 110px !important;
    max-width: 110px !important;
  }
  tr[id$="FEEDBACK_V4"] div,
  tr[id$="FEEDBACK_V3"] div,
  tr[id$="FEEDBACK"] div {
    padding-left: 16px !important;
    padding-bottom: 12px !important;
  }
  /* Thumbs icons: prevent clipping, ensure full display */
  tr[id$="FEEDBACK_V4"] td:not(:first-of-type) img,
  tr[id$="FEEDBACK_V3"] td:not(:first-of-type) img,
  tr[id$="FEEDBACK"] td:not(:first-of-type) img {
    display: inline-block !important;
    vertical-align: middle !important;
    object-fit: contain !important;
    width: 24px !important;
    height: 24px !important;
    max-width: 24px !important;
    max-height: 24px !important;
  }
  tr[id$="FEEDBACK_V4"] td:not(:first-of-type),
  tr[id$="FEEDBACK_V3"] td:not(:first-of-type),
  tr[id$="FEEDBACK"] td:not(:first-of-type) {
    overflow: visible !important;
    min-height: 46px !important;
  }

  /* Footer (FOOTER, FOOTER_V3, FOOTER_V4, NEW_FOOTER): prevent overflow, reduce extra space */
  tr[id$="FOOTER"] table,
  tr[id$="FOOTER_V3"] table,
  tr[id$="FOOTER_V4"] table,
  tr[id$="NEW_FOOTER"] table {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
  tr[id$="FOOTER"] > td,
  tr[id$="FOOTER_V3"] > td,
  tr[id$="FOOTER_V4"] > td,
  tr[id$="NEW_FOOTER"] > td {
    max-width: 100% !important;
    box-sizing: border-box !important;
    padding: 20px 16px 16px !important;
    height: auto !important;
  }
  tr[id$="FOOTER"] .content-block-footer,
  tr[id$="FOOTER_V3"] .content-block-footer,
  tr[id$="FOOTER_V4"] .content-block-footer,
  tr[id$="NEW_FOOTER"] .content-block-footer {
    padding-top: 20px !important;
    padding-bottom: 16px !important;
  }
  tr[id$="FOOTER"] .table-not-adapt,
  tr[id$="FOOTER"] table[class*="social"],
  tr[id$="FOOTER_V3"] .table-not-adapt,
  tr[id$="FOOTER_V3"] table[class*="social"],
  tr[id$="FOOTER_V4"] .table-not-adapt,
  tr[id$="FOOTER_V4"] table[class*="social"],
  tr[id$="NEW_FOOTER"] .table-not-adapt,
  tr[id$="NEW_FOOTER"] table[class*="social"] {
    margin: 16px 0 !important;
  }

  /* Bill Summary (MONTHLY_SUMMARY_V2, MONTHLY_SUMMARY): align arrow with main amount */
  tr[id$="MONTHLY_SUMMARY_V2"] table tr td,
  tr[id$="MONTHLY_SUMMARY"] table tr td {
    vertical-align: bottom !important;
  }
  /* Bill Summary: keep amount column (and green row) on one line — target inner table where amounts live */
  tr[id$="MONTHLY_SUMMARY_V2"] table tr td:last-child,
  tr[id$="MONTHLY_SUMMARY"] table tr td:last-child,
  tr[id$="MONTHLY_SUMMARY_V2"] table table tr td:last-child,
  tr[id$="MONTHLY_SUMMARY"] table table tr td:last-child {
    white-space: nowrap !important;
  }
  /* All section tables: prevent numbers from breaking mid-digit (Bill Summary, Itemization, etc.) */
  tr[id] table td,
  tr[id] table th {
    word-break: normal !important;
    overflow-wrap: normal !important;
  }
  tr[id$="MONTHLY_SUMMARY_V2"] img[src*="Up_down"],
  tr[id$="MONTHLY_SUMMARY_V2"] img[alt*="arrow"],
  tr[id$="MONTHLY_SUMMARY_V2"] img[alt*="Up"],
  tr[id$="MONTHLY_SUMMARY_V2"] img[alt*="Down"],
  tr[id$="MONTHLY_SUMMARY"] img[src*="Up_down"],
  tr[id$="MONTHLY_SUMMARY"] img[alt*="arrow"],
  tr[id$="MONTHLY_SUMMARY"] img[alt*="Up"],
  tr[id$="MONTHLY_SUMMARY"] img[alt*="Down"] {
    vertical-align: middle !important;
    display: inline-block !important;
  }

  /* All section rows: ensure nested tables/divs respect viewport (safety for HEADER, GREETING, MONTHLY_SUMMARY, ITEMIZATION, etc.) */
  tr[id] table,
  tr[id] div {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`;

/**
 * Builds a full HTML document string for rendering email content in an iframe
 * with a mobile viewport. If input has <html>/<body>, extracts body content
 * to avoid nested documents.
 */
function buildEmailMobileDocument(html: string, viewportWidth: number): string {
  if (!html?.trim()) {
    const meta = `<meta charset="UTF-8"><meta name="viewport" content="width=${viewportWidth}, initial-scale=1, maximum-scale=1, user-scalable=no">`;
    return `<!DOCTYPE html><html><head>${meta}</head><body></body></html>`;
  }
  const trimmed = html.trim();
  const looksLikeFullDocument =
    trimmed.toLowerCase().startsWith('<!doctype') || trimmed.toLowerCase().startsWith('<html');
  let bodyContent: string;
  if (looksLikeFullDocument) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      bodyContent = doc.body?.innerHTML ?? doc.documentElement?.innerHTML ?? html;
    } catch {
      bodyContent = html;
    }
  } else {
    bodyContent = html;
  }
  const meta = `<meta charset="UTF-8"><meta name="viewport" content="width=${viewportWidth}, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover">`;
  const style = `<style>${EMAIL_MOBILE_CSS}</style>`;
  return `<!DOCTYPE html><html><head>${meta}${style}</head><body>${bodyContent}</body></html>`;
}

/** Email mobile preview: iPhone 17 Pro Max frame with iframe rendering displayHtml. */
const EmailMobilePreview: React.FC<{ html: string }> = ({ html }) => {
  const theme = useTheme();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(1);

  const srcdoc = React.useMemo(
    () => buildEmailMobileDocument(html, MOBILE_PHONE.width),
    [html]
  );

  const frameW = MOBILE_PHONE.width + MOBILE_PHONE.bezel * 2;
  const frameH = MOBILE_PHONE.height + MOBILE_PHONE.bezel * 2;

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateScale = () => {
      const { clientWidth, clientHeight } = el;
      const s = Math.min(clientWidth / frameW, clientHeight / frameH, MAX_PHONE_SCALE);
      setScale(Math.max(0.2, s));
    };
    updateScale();
    const ro = new ResizeObserver(updateScale);
    ro.observe(el);
    return () => ro.disconnect();
  }, [frameW, frameH]);

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {/* Wrapper with scaled dimensions for layout—transform:scale() does not reduce layout space */}
      <Box
        sx={{
          width: frameW * scale,
          height: frameH * scale,
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: frameW,
            height: frameH,
            transform: `translate(-50%, -50%) scale(${scale})`,
            transformOrigin: 'center center',
            bgcolor: '#000',
            border: `${MOBILE_PHONE.bezel}px solid #000`,
            borderRadius: '44px',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 20px 25px rgba(0,0,0,0.6)'
                : '0 20px 25px rgba(0,0,0,0.4)',
            overflow: 'hidden',
          }}
        >
        <Box
          sx={{
            width: MOBILE_PHONE.width,
            height: MOBILE_PHONE.height,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Dynamic Island - iPhone 17 Pro Max style */}
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 100,
              height: 28,
              borderRadius: 14,
              bgcolor: '#000',
              zIndex: 2,
            }}
          />
          <iframe
            title="Email mobile preview"
            srcDoc={srcdoc}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: '#fff',
              display: 'block',
            }}
          />
        </Box>
        </Box>
      </Box>
    </Box>
  );
};

type VariantConfig = {
  templateKey?: string;
  hierarchy?: string;
  locale?: string;
  scenario?: string;
};

type PreviewFrameProps = {
  variantName?: string;
  variant?: VariantConfig | null;
  renderRequest: RenderRequest | null;
  previewMode: 'desktop' | 'mobile';
  channel?: string;
  isFullTemplateSelected?: boolean;
  renderedHtml?: string;
  renderedSubject?: string;
  loading?: boolean;
  isWebWidgetTemplate?: boolean;
  herPreviewUrl?: string | null; // HER preview URL to display in iframe
  onWebWidgetFinished?: (templateKey: string, result: { success: boolean; error?: string }) => void;
};

export const PreviewFrame: React.FC<PreviewFrameProps> = ({
  variantName,
  variant,
  renderRequest,
  previewMode,
  channel = 'Email',
  isFullTemplateSelected = false,
  renderedHtml,
  renderedSubject,
  loading = false,
  isWebWidgetTemplate = false,
  herPreviewUrl,
  onWebWidgetFinished,
}) => {
  const theme = useTheme();
  const effectiveTemplateKey = variant?.templateKey || renderRequest?.templateKey;
  const refreshKey = renderRequest?.refreshKey ?? null; // Extract refreshKey to trigger re-render on save
  const effectiveElementKey = renderRequest?.elementKey || null;
  const { instanceId, isInitializing, initError, isReady, reinitialize, sdkScriptUrl, getIframeInitConfig } = useWidgetSdk();

  const widgetContainerRef = React.useRef<HTMLDivElement | null>(null);
  // Important: this page can render multiple widgets at once (e.g., 5 predefined widgets).
  // A static id like "widget-container" will collide and can break SDKs that query by element id.
  const reactId = React.useId();
  const widgetContainerDomId = React.useMemo(() => {
    // useId() can include ":" which is valid in HTML but can be awkward in selectors; normalize it.
    const rid = reactId.replace(/[:]/g, '');
    return `widget-container-${rid}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactId]);
  const [selectedWidgetTag, setSelectedWidgetTag] = React.useState<string | null>(null);
  const errorToastShownRef = React.useRef<string | null>(null); // Track which template we've shown error for

  // Resolve widget tag for Web mobile iframe (SDK must be loaded so WIDGET_TAG is available).
  const webMobileWidgetTag = React.useMemo(() => {
    if (!effectiveTemplateKey || typeof window === 'undefined') return null;
    const key = getWidgetIdFromTemplate(effectiveTemplateKey);
    const tags = (window as any).BidgelyWebSdk?.WIDGET_TAG as Record<string, string> | undefined;
    return key && tags ? tags[key] ?? null : null;
  }, [effectiveTemplateKey, isReady]); // isReady so we recompute when SDK loads
  
  // Track template changes, refreshKey, scenario, and previewMode for re-initialization
  const prevTemplateKeyRef = React.useRef<string | null>(null);
  const prevRefreshKeyRef = React.useRef<number | null>(null);
  const prevScenarioIdRef = React.useRef<string | null>(null);
  const prevPreviewModeRef = React.useRef<'desktop' | 'mobile' | null>(null);

  // Get data from Redux
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);
  
  // Extract fuelType from projectAttributes
  const fuelType = React.useMemo(() => {
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      if (typeof projectAttributes.fuel_type === 'string') {
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuelType;
  }, [projectAttributes]);

  const locale = variant?.locale || renderRequest?.variant?.locale || 'en_US';

  // Get scenarios from Redux
  const availableScenarios = useSelector((state: RootState) => {
    if (!effectiveTemplateKey || !selectedPilotId) {
      return [];
    }
    const scenarios = selectScenariosForTemplate(
      state,
      String(selectedPilotId),
      fuelType,
      locale,
      effectiveTemplateKey,
      channel
    );

    return scenarios;
  });

  // Resolve scenario ID: always return an actual scenarioId from DataScenario objects
  // Note: 'default' is just a display name, not a scenarioId
  // availableScenarios are already filtered by template and productType (channel) in the selector
  const resolvedScenarioId = React.useMemo(() => {
    const scenario = variant?.scenario || renderRequest?.variant?.scenario;

    // Special-case: "Web_FullTemplate_1" is a cross-template Web scenario used for rendering multiple widgets
    // (e.g., Color Palette "View All Web Widgets"). It may not appear under each widget's templateName in scenarios,
    // so we must allow it even if it's not present in availableScenarios for the specific widget template.
    if (channel === 'Web' && scenario === 'Web_FullTemplate_1') {
      return scenario;
    }

    // For any channel (including Web): only use an explicitly provided scenario
    // if it exists in the scenarios list for this template. Otherwise, fall back
    // to the template's default scenario to avoid crashing the widget runtime.
    if (scenario && scenario !== 'default') {
      const foundScenario = availableScenarios?.find(ds => ds.scenarioId === scenario);
      if (foundScenario) {
        return foundScenario.scenarioId;
      }
      // Fall through to default scenario resolution
    }
    
    // If no scenarios available, we cannot resolve a scenarioId
    if (!availableScenarios || availableScenarios.length === 0) {
      console.error('[PreviewFrame] No scenarios available - cannot resolve scenarioId');
      return null; // Return null instead of 'default' string
    }
    
    // Note: scenario override (if present) is handled above with a safe existence check.
    
    // Find default scenario: first try defaultScenario flag, then scenarioDisplayName "Default", then first scenario
    // This handles both "default" string and when no scenario is provided
    const defaultScenario = availableScenarios.find(ds => ds.defaultScenario === true) ||
                           availableScenarios.find(ds => ds.scenarioDisplayName === 'Default') ||
                           availableScenarios[0];
    
    if (defaultScenario) {
      return defaultScenario.scenarioId; // Always return actual scenarioId, never 'default' string
    }
    
    // This should never happen if availableScenarios has items
    console.error('[PreviewFrame] Could not find any scenario in availableScenarios');
    return null;
  }, [variant?.scenario, renderRequest?.variant?.scenario, availableScenarios, channel, effectiveTemplateKey]);

  // Get loading state to avoid showing error while scenarios are still loading
  const scenariosLoading = useSelector((state: RootState) => {
    if (!selectedPilotId) return false;
    return selectDataScenariosLoading(
      state,
      String(selectedPilotId),
      fuelType,
      locale
    );
  });

  // Show error toast when no data scenario is found for Web widget
  // Use a delayed approach to avoid showing toast during rapid state changes
  React.useEffect(() => {
    // Only show error for Web widgets when:
    // 1. Template is selected
    // 2. Scenarios are not loading
    // 3. SDK is ready (to avoid showing during initial load)
    // 4. No scenarios available
    if (
      channel === 'Web' &&
      isWebWidgetTemplate &&
      effectiveTemplateKey &&
      !scenariosLoading &&
      isReady && // Only show when SDK is ready
      (!availableScenarios || availableScenarios.length === 0)
    ) {
      // Only show toast once per template to avoid spam
      if (errorToastShownRef.current !== effectiveTemplateKey) {
        // Add a delay to avoid showing toast during rapid state changes or initial load
        // This ensures we only show the error when the user is actually trying to view the widget
        const timeoutId = setTimeout(() => {
          // Double-check conditions after delay to ensure we still need to show the error
          if (
            channel === 'Web' &&
            isWebWidgetTemplate &&
            effectiveTemplateKey &&
            !scenariosLoading &&
            isReady &&
            (!availableScenarios || availableScenarios.length === 0) &&
            errorToastShownRef.current !== effectiveTemplateKey
          ) {
        errorToastShownRef.current = effectiveTemplateKey;
            // Use warning instead of error to be less aggressive
            toast.warning(
              `No data scenario found for widget "${effectiveTemplateKey}". Please configure a data scenario in the Data Scenarios section.`
        );
          }
        }, 2000); // Wait 2 seconds before showing toast to ensure stable state

        return () => {
          clearTimeout(timeoutId);
        };
      }
    } else if (availableScenarios && availableScenarios.length > 0) {
      // Reset the ref when scenarios become available
      errorToastShownRef.current = null;
    }
  }, [
    channel,
    isWebWidgetTemplate,
    effectiveTemplateKey,
    availableScenarios,
    scenariosLoading,
    isReady, // Add isReady to dependencies
  ]);

  // Render widget when SDK is ready and template is selected
  React.useEffect(() => {
    if (channel !== 'Web' || !isWebWidgetTemplate) {
      setSelectedWidgetTag(null);
      if (widgetContainerRef.current) {
        widgetContainerRef.current.innerHTML = '';
      }
      return;
    }

    if (!effectiveTemplateKey || !isReady || !instanceId || !resolvedScenarioId) {
      return;
    }

    // Re-initialize on template change, refreshKey change, or preview mode switch (so mobile iframe gets fresh config even when user never switches to desktop)
    const templateChanged = effectiveTemplateKey !== prevTemplateKeyRef.current && prevTemplateKeyRef.current !== null;
    const refreshKeyChanged = refreshKey !== null && refreshKey !== prevRefreshKeyRef.current && prevRefreshKeyRef.current !== null;
    const previewModeChanged = prevPreviewModeRef.current !== null && prevPreviewModeRef.current !== previewMode;
    const shouldReinitialize = templateChanged || refreshKeyChanged || previewModeChanged;

    // Web mobile uses iframe-based preview; widget is rendered inside the iframe. Still trigger reinitialize when needed so iframe gets fresh init config.
    if (previewMode === 'mobile') {
      if (shouldReinitialize) {
        reinitialize().catch(err => console.error('[PreviewFrame] Re-initialize for mobile failed:', err));
      }
      return;
    }

    if (!widgetContainerRef.current) {
      return;
    }

    // Prevent duplicate renders for the same widget + scenario + instance + refreshKey in dev StrictMode
    const renderSignature = `${effectiveTemplateKey}|${instanceId}|${resolvedScenarioId}|${String(refreshKey ?? '')}`;
    const alreadyRendered =
      widgetContainerRef.current &&
      widgetContainerRef.current.childNodes &&
      widgetContainerRef.current.childNodes.length > 0;
    if (prevScenarioIdRef.current === resolvedScenarioId && prevTemplateKeyRef.current === effectiveTemplateKey && prevRefreshKeyRef.current === refreshKey && alreadyRendered) {
      return;
    }

    // Use unified widget renderer utility
    initializeAndRenderWidget({
      templateKey: effectiveTemplateKey,
      scenarioId: resolvedScenarioId,
      instanceId: instanceId,
      containerRef: widgetContainerRef,
      reinitialize: shouldReinitialize ? reinitialize : undefined, // Re-initialize on template change, save, or desktop/mobile switch
      onRenderComplete: (response) => {
        // Get widget tag for state tracking
        const widgetKey = getWidgetIdFromTemplate(effectiveTemplateKey);
        if (widgetKey && window.BidgelyWebSdk?.WIDGET_TAG?.[widgetKey]) {
          setSelectedWidgetTag(window.BidgelyWebSdk.WIDGET_TAG[widgetKey]);
        }
      },
      onError: (error) => {
        console.error('[PreviewFrame] Widget render error:', error);
        // Clear container on error
        if (widgetContainerRef.current) {
          widgetContainerRef.current.innerHTML = '';
        }
      }
    }).then((result) => {
      if (result.success && result.widgetTag) {
        setSelectedWidgetTag(result.widgetTag);
      }
      if (onWebWidgetFinished && effectiveTemplateKey) {
        onWebWidgetFinished(effectiveTemplateKey, { success: result.success, error: result.error });
      }
    }).catch((error) => {
      console.error('[PreviewFrame] Fatal error in widget rendering:', error);
      if (onWebWidgetFinished && effectiveTemplateKey) {
        onWebWidgetFinished(effectiveTemplateKey, { success: false, error: error?.message || String(error || '') });
      }
    });

    // Cleanup function to remove widget on unmount or template change
    return () => {
      if (widgetContainerRef.current) {
        widgetContainerRef.current.innerHTML = '';
      }
      setSelectedWidgetTag(null);
    };
  }, [channel, isWebWidgetTemplate, effectiveTemplateKey, isReady, instanceId, resolvedScenarioId, refreshKey, reinitialize, previewMode]); // previewMode: re-render widget when switching desktop/mobile so the correct container (ref) gets the widget
  
  // Track template, refreshKey, scenario, and previewMode to update refs (for determining when to re-initialize)
  React.useEffect(() => {
    if (effectiveTemplateKey && effectiveTemplateKey !== prevTemplateKeyRef.current) {
      prevTemplateKeyRef.current = effectiveTemplateKey;
    }
    if (refreshKey !== null && refreshKey !== prevRefreshKeyRef.current) {
      prevRefreshKeyRef.current = refreshKey;
    }
    if (resolvedScenarioId && resolvedScenarioId !== prevScenarioIdRef.current) {
      prevScenarioIdRef.current = resolvedScenarioId;
    }
    prevPreviewModeRef.current = previewMode;
  }, [effectiveTemplateKey, refreshKey, resolvedScenarioId, previewMode]);

  const webWidgetDisplayName = React.useMemo(
    () => getWebWidgetDisplayName(effectiveTemplateKey ?? null),
    [effectiveTemplateKey]
  );

  const subjectLine = renderedSubject || null;

  const displayHtml = React.useMemo(() => {
    // Only for Email teaser: reveal hidden preheader/teaser text so users can see/edit it.
    if (channel !== 'Email') return renderedHtml;
    if (!renderedHtml) return renderedHtml;
    if (effectiveElementKey !== 'TEASER') return renderedHtml;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(renderedHtml, 'text/html');
      const nodes = Array.from(doc.querySelectorAll('tr,td,div,span'));
      let changed = 0;
      for (const el of nodes) {
        const style = el.getAttribute('style') || '';
        const hasHidden =
          style.includes('mso-hide:all') ||
          style.includes('display:none') ||
          style.includes('visibility:hidden') ||
          style.includes('max-height:0') ||
          style.includes('opacity:0');
        if (!hasHidden) continue;

        // Heuristic: only reveal nodes that are meant to be preheader/teaser
        // (they are usually hidden via mso-hide/display:none and contain teaser text).
        const text = (el.textContent || '').trim();
        if (!text) continue;

        const newStyle = [
          // Force visible
          'display:block !important',
          'visibility:visible !important',
          'max-height:none !important',
          'max-width:none !important',
          'opacity:1 !important',
          'overflow:visible !important',
          // Make readable
          'font-size:14px !important',
          'line-height:1.4 !important',
          'color:#111 !important',
          // Add subtle frame so users know it’s normally hidden
          'background:#fffbe6',
          'border:1px dashed #d0d0d0',
          'padding:8px',
          'margin:8px 0',
        ].join('; ');
        el.setAttribute('style', newStyle);
        changed++;
      }

      // Re-serialize
      return doc.documentElement?.outerHTML || renderedHtml;
    } catch {
      return renderedHtml;
    }
  }, [channel, effectiveElementKey, renderedHtml]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
      {!renderRequest && (
        <Box sx={{ padding: 1.5, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">Select a template to preview</Typography>
        </Box>
      )}
      {variantName && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            padding: '6px 12px',
            borderRadius: 1.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: theme => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(0, 0, 0, 0.02)',
            color: 'text.secondary',
            fontSize: '12px',
            fontWeight: 500
          }}
        >
          <Typography component="span" variant="caption">{variantName}:</Typography>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.hierarchy || 'Project'}
          </Box>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.locale || 'en_US'}
          </Box>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.scenario || 'default'}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: 2,
          bgcolor: theme => theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 5,
              color: 'text.secondary'
            }}
          >
            <Typography variant="body2">Loading preview...</Typography>
          </Box>
        )}

        {!loading && channel === 'Email' && subjectLine && isFullTemplateSelected && (
          <Card
            sx={{
              marginBottom: 2,
              boxShadow: theme => theme.palette.mode === 'dark'
                ? '0 4px 6px rgba(0,0,0,0.3)'
                : '0 4px 6px rgba(0,0,0,0.1)',
              bgcolor: 'background.paper'
            }}
          >
            <CardHeader
              sx={{ padding: 1.5 }}
              title={
                <Box
                  component="label"
                  htmlFor={`subject-line-preview-${variantName || 'main'}`}
                sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'text.secondary',
                  display: 'block',
                  marginBottom: 0.5
                }}
              >
                Subject Line
                </Box>
              }
              subheader={
                <Typography
                  id={`subject-line-preview-${variantName || 'main'}`}
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    margin: 0
                  }}
                >
                {subjectLine}
              </Typography>
              }
            />
          </Card>
        )}

        {/* Web widget path: direct render (no iframe, same as WidgetsSdkDemoPage) */}
        {!loading && channel === 'Web' && isWebWidgetTemplate ? (
          previewMode === 'mobile' ? (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <MobilePreview showPhoneFrame>
                <WidgetErrorBoundary>
                  {initError && (
                    <Box sx={{ padding: 2, textAlign: 'center', color: 'error.main' }}>
                      <Typography variant="body2">Error: {initError}</Typography>
                    </Box>
                  )}
                  {!initError && (
                    <WebWidgetMobileIframe
                      sdkScriptUrl={sdkScriptUrl}
                      scenarioId={resolvedScenarioId ?? ''}
                      widgetTag={webMobileWidgetTag}
                      iframeInitConfig={getIframeInitConfig()}
                      displayName={webWidgetDisplayName}
                      isSdkInitializing={isInitializing}
                      isSdkReady={isReady}
                    />
                  )}
                </WidgetErrorBoundary>
              </MobilePreview>
            </Box>
          ) : (
            <WidgetErrorBoundary>
              <Box
                sx={{
                  margin: '0 auto',
                  bgcolor: 'background.paper',
                  boxShadow: theme => theme.palette.mode === 'dark'
                    ? '0 4px 6px rgba(0,0,0,0.3)'
                    : '0 4px 6px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  borderRadius: 2,
                  width: `${getWebWidgetDesktopWidth(effectiveTemplateKey ?? null)}px`,
                  minWidth: `${getWebWidgetDesktopWidth(effectiveTemplateKey ?? null)}px`,
                  maxWidth: '100%',
                  minHeight: 400,
                  padding: 2
                }}
              >
                <div
                  ref={widgetContainerRef}
                  id={widgetContainerDomId}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '400px'
                  }}
                />
                {isInitializing && (
                  <Box sx={{ padding: 2, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">Initializing {webWidgetDisplayName}…</Typography>
                  </Box>
                )}
                {initError && (
                  <Box sx={{ padding: 2, textAlign: 'center', color: 'error.main' }}>
                    <Typography variant="body2">Error: {initError}</Typography>
                  </Box>
                )}
                {!isInitializing && !initError && !isReady && (
                  <Box sx={{ padding: 2, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">Loading {webWidgetDisplayName}…</Typography>
                  </Box>
                )}
              </Box>
            </WidgetErrorBoundary>
          )
        ) : !loading && channel === 'HER' && herPreviewUrl ? (
          // Render HER preview in iframe
          <Box
            sx={{
              width: '100%',
              height: '100%',
              minHeight: '600px',
              bgcolor: 'background.paper',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <iframe
              src={herPreviewUrl}
              title="HER Preview"
              style={{
                width: '100%',
                height: '100%',
                minHeight: '600px',
                border: 'none',
                backgroundColor: '#ffffff'
              }}
            />
          </Box>
        ) : !loading && renderedHtml ? (
          // Render HTML from API - apply mobile or desktop styling based on previewMode
          previewMode === 'mobile' && channel !== 'HER' ? (
            <Box sx={{ flex: 1, minHeight: 500, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <EmailMobilePreview html={displayHtml || ''} />
            </Box>
          ) : (
            // Desktop preview
            <Box
              sx={{
                margin: '0 auto',
                bgcolor: 'background.paper',
                boxShadow: theme => theme.palette.mode === 'dark'
                  ? '0 4px 6px rgba(0,0,0,0.3)'
                  : '0 4px 6px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                borderRadius: 2,
                ...(channel === 'Web' 
                  ? { minWidth: '1152px' }
                  : { maxWidth: '672px' })
              }}
            >
              <Box
                dangerouslySetInnerHTML={{ __html: displayHtml || '' }}
                sx={{
                  width: '100%',
                  maxWidth: '100%'
                }}
              />
            </Box>
          )
        ) : !loading && (
          // Show message if no rendered HTML available
          <Box sx={{ padding: 1.5, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No preview available</Typography>
          </Box>
        )}
        </Box>
      </Box>
    </Box>
  );
};

